// eslint-disable-next-line import/no-extraneous-dependencies
import classNames from "classnames";
import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { RiCloseCircleLine } from "@remixicon/react";
import { useTicketState } from "../../../../contexts/ticket-state/TicketState";
import Styles from "./BulkActions.module.scss";
import Button from "../../../theme/button/Button";
import {
    ActionContext, ActionHandler, HandlerButton, Handler, HANDLER_INTERFACE,
} from "../../../../contexts/ticket-state/action-registry";
import OverflowMenu from "../../../overflow-menu/OverflowMenu";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";

function ActionButton({ button, handler }: { button: HandlerButton, handler: Handler }) {
    const metricPath = useMemo(() => [
        LoggingHandler.Metrics.AGENT, "bulk-actions", button.text.toLowerCase().replace(/ /g, "-"),
    ], [button]);
    const ticketContext = useTicketState();
    const onClick = useCallback(() => {
        const selection = ticketContext.selectionState.current;
        const container = new ActionHandler(selection, ActionContext.BULK_ACTION, () => {
            ticketContext.selectionState.clear();
            setTimeout(() => {
                selection.firstElement.highlight();
                setTimeout(() => selection.forEach(ticket => ticket.highlight()), 500);
            }, 200);
            LoggingHandler.instance.metric([...metricPath, "done"]);
        }, () => {
            ticketContext.selectionState.setSelection(selection);
        });
        handler.handleBulkAction(container);
    }, [handler]);

    if (button.render) {
        return button.render({ button, onClick });
    }

    return (
        <Button
            size="small"
            onClick={onClick}
            className="action"
            variant={button.variant}
            prefixIcon={button.icon}
            metricPath={metricPath}
        >
            {button.text}
        </Button>
    );
}

export default function BulkActionsMenu() {
    const ticketContext = useTicketState();
    const [count, setCount] = useState(0);
    useEffect(() => {
        setCount(ticketContext.selectionState?.current.length);
    }, [ticketContext.selectionState?.current]);

    const actions = ticketContext.selectionState?.getPossibleActions(ActionContext.BULK_ACTION);

    return (
        <div
            className={classNames(Styles["bulk-action-menu"], {
                show: ticketContext.selectionState?.current.length > 0,
            })}
        >
            <Button
                className="clear-selection"
                postfixIcon={<RiCloseCircleLine />}
                size="small"
                variant="ghost-gray"
                metricPath={[LoggingHandler.Metrics.AGENT, "bulk-actions", "clear-selection"]}
                onClick={() => {
                    ticketContext.selectionState.clear();
                }}
            >
                {count}
                {" "}
                Selected
            </Button>

            <OverflowMenu
                className={Styles["overflow-container"]}
                dropdownClassName="action"
                toggleSettings={{ size: "small", variant: "ghost-gray" }}
            >
                {actions.map(({ button, handler }) => (
                    !button.canNotOverflow && (<ActionButton key={button.text} button={button} handler={handler} />)
                ))}
            </OverflowMenu>

            {actions.map(({ button, handler }) => (
                button.canNotOverflow && (<ActionButton key={button.text} button={button} handler={handler} />)
            ))}
        </div>

    );
}

HANDLER_INTERFACE.register(BulkActionsMenu);

BulkActionsMenu.defaultProps = {
    isDragging: false,
    isHidden: false,
};
